import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { Auth } from '@shared/auth';
import { Observable } from 'rxjs/Observable';
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators';

import { DATASTORE_CONFIG } from './datastore.config';
import { DatastoreConfig } from './datastore.interface';

import {
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  BackendSetPayloadTypes,
  BackendUpdatePayloadTypes,
} from '../backend.model';
import { Collections, SearchCollections } from '../collections.model';
import {
  BackendDeleteRequest,
  BackendFetchRequest,
  BackendPushRequest,
  BackendSetRequest,
  BackendUpdateRequest,
  Params,
} from './backend';
import { isDefined } from './helpers';
import { RawQuery } from './store.model';

export type ApiResponseStatus = 'success' | 'error';

export type ApiFetchResponse<
  K extends keyof (Collections & SearchCollections)
> =
  | { status: 'success'; result: BackendFetchTypes[K] }
  | { status: 'error'; message: string };

export type ApiPostResponse<K extends keyof Collections> =
  | { status: 'success'; result: BackendPushReturnTypes[K] }
  | { status: 'error'; message: string };

@Injectable()
export class ApiHttp {
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandler,
    private auth: Auth,
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
  ) {}

  fetch<K extends keyof Collections>(
    request: BackendFetchRequest,
    query?: RawQuery<Collections>,
  ): Observable<ApiFetchResponse<K>>;

  fetch<K extends keyof (Collections & SearchCollections)>(
    request: BackendFetchRequest,
  ): Observable<ApiFetchResponse<K>>;

  fetch<K extends keyof (Collections & SearchCollections)>(
    r: BackendFetchRequest,
    query?: RawQuery<any>,
  ): Observable<ApiFetchResponse<K>> {
    return Observable.of(r).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([request, authHeader]: [BackendFetchRequest, HttpHeaders]) => {
        const baseUrl = request.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const pagination = {
          limit: query && query.limit,
          offset: query && query.offset,
        };

        // Add webapp=1 for tracking in the backend to API requests
        const requestParams = request.isGaf
          ? request.params || {}
          : { ...(request.params || {}), webapp: 1 };
        const params = pagination
          ? {
              limit: pagination.limit,
              offset: pagination.offset,
              ...requestParams,
            }
          : requestParams;

        return this.http.get<ApiFetchResponse<K>>(
          `${baseUrl}/${request.endpoint}`,
          {
            headers: authHeader,
            params: this.serialize(params),
          },
        );
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          this.errorHandler.handleError(err.error);
          // FIXME: not sure why TS can't figure out the typings here
          return Observable.of(<{ status: 'error'; message: string }>{
            status: 'error',
            message: err.error.message,
          });
        }
        // The backend returned an unsuccessful response code.
        this.errorHandler.handleError(new Error(err.message));
        // FIXME: not sure why TS can't figure out the typings here
        return Observable.of(<{ status: 'error'; message: string }>{
          status: 'error',
          message: err.message,
        });
      }),
    );
  }

  post<K extends keyof Collections>(
    request:
      | BackendPushRequest<K>
      | BackendSetRequest<K>
      | BackendUpdateRequest<K>
      | BackendDeleteRequest<K>,
  ): Observable<ApiPostResponse<K>> {
    return this.mutate(request, { action: 'post', payload: request.payload });
  }

  put<K extends keyof Collections>(
    request: BackendUpdateRequest<K> | BackendDeleteRequest<K>,
  ): Observable<ApiPostResponse<K>> {
    return this.mutate(request, { action: 'put', payload: request.payload });
  }

  delete<K extends keyof Collections>(
    request: BackendDeleteRequest<K>,
  ): Observable<ApiPostResponse<K>> {
    return this.mutate(request, { action: 'delete' });
  }

  private mutate<K extends keyof Collections>(
    request:
      | BackendPushRequest<K>
      | BackendSetRequest<K>
      | BackendUpdateRequest<K>
      | BackendDeleteRequest<K>,
    data:
      | {
          action: 'post';
          payload:
            | BackendPushPayloadTypes[K]
            | BackendSetPayloadTypes[K]
            | BackendUpdatePayloadTypes[K];
        }
      | { action: 'put'; payload: BackendUpdatePayloadTypes[K] }
      | { action: 'delete' },
  ): Observable<ApiPostResponse<K>> {
    return Observable.of(request).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([newRequest, authHeader]) => {
        const baseUrl = newRequest.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const url = `${baseUrl}/${newRequest.endpoint}`;
        const options = { headers: authHeader };

        if (data.action === 'delete') {
          return this.http.delete<ApiPostResponse<K>>(url, options);
        }

        const body = {
          ...(<Params>data.payload), // TODO: https://github.com/Microsoft/TypeScript/issues/15300
          ...newRequest.params,
        };

        if (data.action === 'post') {
          return this.http.post<ApiPostResponse<K>>(
            url,
            // default to JSON
            <boolean>(<any>request).asJSON ? body : this.serialize(body),
            options,
          );
        }

        // FIXME, make put support JSON as well.
        return this.http.put<ApiPostResponse<K>>(
          url,
          this.serialize(body),
          options,
        );
      }),
    );
  }

  private serialize(obj: Partial<Params>): HttpParams {
    let params = new HttpParams();
    Object.entries(obj)
      .filter(isDefined)
      .forEach(([key, value]) => {
        if (typeof value === 'string') {
          params = params.set(key, value);
        } else if (typeof value === 'number') {
          params = params.set(key, String(value));
        } else if (Array.isArray(value)) {
          // This type casting shouldn't be necessary but is because of a TS bug.
          // https://github.com/Microsoft/TypeScript/issues/15315
          // https://github.com/Microsoft/TypeScript/issues/7294

          // Our backend expects arrays foo=[1,2] to be encoded as foo[]=1&foo[]=2
          (value as (string | number | undefined)[])
            .filter(isDefined)
            .forEach(rawVal =>
              // The batching of requests connatenates things with `&`.
              String(rawVal)
                .split('&')
                .forEach(v => (params = params.append(`${key}[]`, v))),
            );
        }
      });
    params = params.set('compact', 'true');
    return params;
  }

  /*
  private handleException(error: Response | any) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
  */
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/api-http.service.ts